/**
 * Durations are whole milliseconds everywhere. The Swift app stores interval
 * lengths and elapsed time in milliseconds and reconciles rounding so the sum
 * of recorded entries equals the session's active duration exactly; the web
 * port keeps the same unit so recorded sessions stay comparable.
 */
export type Milliseconds = number;

export const SECOND: Milliseconds = 1_000;
export const MINUTE: Milliseconds = 60 * SECOND;

/**
 * Formats a duration the way every clock and duration label in the app reads
 * it: `m:ss` under an hour, `h:mm:ss` at or above it. Negative input clamps to
 * zero rather than rendering a minus sign into the round clock.
 */
export function formatDuration(duration: Milliseconds): string {
  const totalSeconds = Math.max(0, Math.round(duration / SECOND));
  const hours = Math.floor(totalSeconds / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  const seconds = totalSeconds % 60;
  const paddedSeconds = String(seconds).padStart(2, "0");

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${paddedSeconds}`;
  }
  return `${minutes}:${paddedSeconds}`;
}

/**
 * Spoken form for assistive technology, which must not read "4:05" as a time
 * of day.
 */
export function spokenDuration(duration: Milliseconds): string {
  const totalSeconds = Math.max(0, Math.round(duration / SECOND));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const parts: string[] = [];
  if (minutes > 0) parts.push(`${minutes} minute${minutes === 1 ? "" : "s"}`);
  if (seconds > 0 || minutes === 0) {
    parts.push(`${seconds} second${seconds === 1 ? "" : "s"}`);
  }
  return parts.join(" ");
}
